import { Routes, ExtraOptions } from '@angular/router';

import { devRoutes } from './dev-routes';

import { LandingPageComponent } from '@core/components/landing-page/landing-page.component';

import { JwtAuthGuard } from '@shared/guards/jwt-auth/jwt-auth.guard';
import { OutCustomizationGuard } from '@shared/guards/out-customization/out-customization.guard';
import { OutSnippetsGuard } from '@shared/guards/out-snippets/out-snippets.guard';
import { ChangePasswordGuard } from '@shared/guards/change-password/change-password.guard';

import { UserPermissionsResolver } from '@shared/resolves/user-permissions-resolver/user-permissions-resolver';
import { ConceptResolver } from '@shared/resolves/concept-resolver/concept-resolver';
import { FieldContainerResolver } from '@shared/resolves/fieldcontainer-resolver/fieldcontainer-resolver';
import { FullMenuConceptResolver } from '@shared/resolves/full-menu-concept-resolve/full-menu-concept-resolve';
import { ProcessResolver } from '@shared/resolves/process-resolver/process-resolver';
import { EvaluationResolver } from '@shared/resolves/evaluation-resolve/evaluation-resolve';
import { OpenLoaderResolver } from '@shared/resolves/loader-resolver/loader-resolver';
import { ConceptObjectResolver } from '@shared/resolves/concept-object-resolver/concept-object-resolver';
import { ConceptByIdResolver } from '@shared/resolves/concept-by-id-resolver/concept-by-id-resolver';
import { AssistResolver } from '@shared/resolves/assist-resolver/assist-resolver';
import { FolderResolver } from '@shared/resolves/folder-resolver/folder-resolver';
import { OrganizationsResolver } from '@shared/resolves/organizations-resolver/organizations-resolver';
import { EvaluationTreeResolver } from '@shared/resolves/evaluation-tree-resolver/evaluation-tree-resolver';
import { OfficeOnlineResolver } from '@shared/resolves/office-online-resolver/office-online-resolver';
import { ReportRecordDetailResolver } from '@shared/resolves/report-record-detail-resolver/report-record-detail-resolver';
import { CompanyPermissionsResolver } from '@shared/resolves/company-permissions-resolver/company-permissions-resolver';
import { RolesProductResolver } from '@shared/resolves/roles-product-resolver/roles-product-resolver.resolver';
import { TaskResolve } from '@shared/resolves/task-resolve/task-resolve';
import { reportResolve } from '@shared/resolves/report-resolver/report-resolver';
import { reportColumnsResolve } from '@shared/resolves/report-columns-resolver/report-columns-resolver';
import { reportRowsResolve } from '@shared/resolves/report-rows-resolver/report-rows-resolver';
import { reportObjectsListResolve } from '@shared/resolves/report-objects-list-resolve/report-objects-list-resolver';
import { generalReportsWhResolve } from '@shared/resolves/general-reports-wh-resolver/general-reports-wh-resolver';
import { GlobalCustomizationResolver } from '@shared/resolves/global-customization-resolver/global-customization-resolver.resolver';
import { filterListResolver } from '@shared/resolves/filterList-resolver/filter-list-resolver';
import { productInfoResolve } from '@shared/resolves/product-info-resolver/product-info-resolver';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'prefix', //default
    redirectTo: 'dashboard'
  },
  {
    path: 'dashboard',
    loadComponent: () => import('./core/components/dashboard-view/dashboard-view.component').then((m) => m.DashboardViewComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'dashboard',
      path: 'dashboard',
      componentName: 'dashboardPanel'
    },
    resolve: {
      companyPermissions: CompanyPermissionsResolver,
    }
  },
  {
    path: 'login',
    loadComponent: () => import('./core/components/front-login/front-login/front-login.component').then((m) => m.FrontLoginComponent),
    data: {
      name: 'login',
      path: 'login',
      componentName: 'frontLogin'
    }
  },
  {
    path: 'login/:target',
    loadComponent: () => import('./core/components/front-login/front-login/front-login.component').then((m) => m.FrontLoginComponent),
    data: {
      name: 'login',
      path: 'login',
      componentName: 'frontLogin'
    }
  },
  {
    path: 'dfa',
    loadComponent: () => import('./core/components/front-login/front-login/front-login.component').then((m) => m.FrontLoginComponent),
    data: {
      name: 'dfa',
      path: 'dfa',
      componentName: 'frontLogin'
    },
  },
  {
    path: 'import-evaluation',
    loadComponent: () => import('./core/components/import-evaluation/import-evaluation.component').then((m) => m.ImportEvaluationComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'import-evaluation',
      path: 'import-evaluation',
      componentName: 'evaluationImport'
    }
  },
  {
    path: 'organizations',
    loadComponent: () => import('./core/components/clients-list/clients-list-default/clients-list.component').then((m) => m.ClientsListComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'organizations',
      path: 'organizations',
      componentName: 'clientsList'
    },
    resolve: {
     
      organizations: OrganizationsResolver
    }
  },
  {
    path: 'evaluation'
  },
  {
    path: 'evaluation/:evaluationId',
    loadComponent: () => import('./core/components/evaluation/evaluation.component').then((m) => m.EvaluationComponent),
    canActivate: [JwtAuthGuard],
    runGuardsAndResolvers: 'always',
    data: {
      name: 'evaluation',
      path: 'evaluation/:evaluationId',
      componentName: 'evaluation'
    },
    resolve: {
      userPermissions: UserPermissionsResolver,
      evaluationTree: EvaluationTreeResolver,
      evaluationInfo: EvaluationResolver
    },

    children: [
      {
        path: 'redirect',
      },
      {
        path: 'folder/:folderId',
        loadComponent: () => import('./core/components/folder/folder.component').then((m) => m.FolderComponent),
        runGuardsAndResolvers: 'always',
        data: {
          name: 'folder',
          path: 'folder/:folderId',
          componentName: 'folder'
        },
        resolve: {
          folder: FolderResolver
        },
      },
      {
        path: 'folder/:folderId/concept/:conceptId/concept-evaluated/:conceptObjectId',
        loadComponent: () => import('./core/components/concept/concept.component').then((m) => m.ConceptComponent),
        data: {
          evaluationTree: null,
          name: 'evaluationConcept',
          path: 'folder/:folderId/concept/:conceptId/concept-evaluated/:conceptObjectId',
          to: 'general',
          componentName: 'concept'
        },
        resolve: {
          conceptInfo: ConceptResolver,
          fieldContainerInfo: FieldContainerResolver,
          fullMenu: FullMenuConceptResolver,
          openLoader: OpenLoaderResolver
        }
      },
      {
        path: 'folder/:folderId/concept/:conceptId/:to',
        loadComponent: () => import('./core/components/edit-concept/edit-concept.component').then((m) => m.EditConceptComponent),
        data: {
          name: 'evaluationEditConcept',
          path: 'folder/:folderId/concept/:conceptId/:to',
          componentName: 'editConcept'
        },
        resolve: {
          conceptInfo: ConceptResolver,
          processInfo: ProcessResolver,
          fieldContainerInfo: FieldContainerResolver,
          fullMenu: FullMenuConceptResolver
        }
      },
      {
        path: 'reports',
        loadComponent: () => import('./core/components/reports/reports.component').then((m) => m.ReportsComponent),
        data: {
          name: 'evaluationReports',
          path: 'reports',
          componentName: 'reports'
        }
      },
      {
        path: 'reports/:reportId',
        loadComponent: () => import('./shared/components/report-info/report-info.component').then((m) => m.ReportInfoComponent),
        data: {
          name: 'evaluationReportInfoIcam',
          path: 'reports/:reportId',
          componentName: 'reportInfo'
        },
        resolve: {
          report: reportResolve
        }
      },
      {
        path: 'reports/:reportId/report-related/:reportObjectId',
        loadComponent: () => import('./shared/components/report-info/report-info.component').then((m) => m.ReportInfoComponent),
        data: {
          name: 'evaluationReportInfo',
          path: 'reports/:reportId/report-related/:reportObjectId',
          componentName: 'reportInfo'
        },
        resolve: {
          report: reportResolve
        },
      }, {
        path: 'reports/:reportId/report-related/:reportObjectId/archived',
        loadComponent: () => import('./shared/components/report-info/report-info.component').then((m) => m.ReportInfoComponent),
        data: {
          name: 'evaluationReportInfoArchived',
          path: 'reports/:reportId/report-related/:reportObjectId/archived',
          componentName: 'reportInfo',
          archived: 'archived'
        },
        resolve: {
          report: reportResolve
        }
      },
      {
        path: 'folder/:folderId/concept/:conceptId/concept-evaluated/:conceptObjectId/tasks/:taskId',
        loadComponent: () => import('./core/components/concept/concept.component').then((m) => m.ConceptComponent),
        data: {
          name: 'evaluationConceptTask',
          path: 'folder/:folderId/concept/:conceptId/concept-evaluated/:conceptObjectId/tasks',
          componentName: 'concept'
        },
        resolve: {
          conceptInfo: ConceptResolver,
          fieldContainerInfo: FieldContainerResolver,
          fullMenu: FullMenuConceptResolver,
          openLoader: OpenLoaderResolver
        }
      },
      {
        path: 'folder/:folderId/concept/:conceptId/concept-evaluated/:conceptObjectId/:to',
        loadComponent: () => import('./core/components/concept/concept.component').then((m) => m.ConceptComponent),
        data: {
          name: 'evaluationConceptOptions',
          path: 'folder/:folderId/concept/:conceptId/concept-evaluated/:conceptObjectId/:to',
          componentName: 'concept'
        },
        resolve: {
          conceptInfo: ConceptResolver,
          fieldContainerInfo: FieldContainerResolver,
          fullMenu: FullMenuConceptResolver,
          openLoader: OpenLoaderResolver
        }
      },
      {
        path: 'favorites',
        loadComponent: () => import('./core/components/favorites/favorites.component').then((m) => m.FavoritesComponent),
        data: {
          name: 'evaluationFavorites',
          path: 'favorites',
          componentName: 'favorites'
        }
      }
    ]
  },
  {
    path: 'roles/:productId',
    loadComponent: () => import('./core/components/roles-window/roles-window.component').then((m) => m.RolesWindowComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'roles',
      path: 'roles',
      componentName: 'rolesWindow'
    },
    resolve: {
      rolesProduct: RolesProductResolver,
      productInfo: productInfoResolve
    }
  },
  {
    path: 'customization',
    loadComponent: () => import('./core/components/global-customization/global-customization.component').then((m) => m.GlobalCustomizationComponent),
    canActivate: [JwtAuthGuard, OutCustomizationGuard],
    data: {
      name: 'customization',
      path: 'customization',
      componentName: 'globalCustomization'
    },
    resolve: {
      companyCustomization: GlobalCustomizationResolver
    }
  },
  {
    path: 'preview-customization',
    loadComponent: () => import('./core/components/preview-customization/preview-customization.component').then((m) => m.PreviewCustomizationComponent),
    canActivate: [JwtAuthGuard, OutCustomizationGuard],
    data: {
      name: 'previewCustomization',
      path: 'preview-customization',
      componentName: 'previewCustomization',
      isLanding: true
    },
    resolve: {
      userProductPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'history-viewer',
    loadComponent: () => import('./shared/components/history-viewer/history-viewer.component').then((m) => m.HistoryViewerComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'historyViewer',
      path: 'history-viewer',
      componentName: 'historyViewer'
    }
  },
  {
    path: 'comparator-viewer',
    loadComponent: () => import('./core/components/edition-comparator/edition-comparator.component').then((m) =>m.EditionComparatorComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'comparatorViewer',
      path: 'comparator-viewer',
      componentName: 'editionComparator'
    }
  },
  {
    path: 'task/:taskId',
    loadComponent: () => import('./shared/components/task-generic/task-generic.component').then((m) => m.TaskGenericComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'task',
      path: 'task/:taskId',
      componentName: 'genericTask',
      genericTask: true
    },
    resolve: {
      task: TaskResolve,
      userProductPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'task',
    loadComponent: () => import('./shared/components/task-generic/task-generic.component').then((m) => m.TaskGenericComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'task',
      path: 'task/:taskId',
      componentName: 'genericTask',
      genericTask: true
    },
    resolve: {
      task: TaskResolve,
      userProductPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'clients-tracking',
    loadComponent: () => import('./shared/components/clients-tracking/clients-tracking.component').then((m) => m.ClientsTrackingComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'clientsTracking',
      path: 'clients-tracking',
      componentName: 'clientsTracking'
    },
    resolve: {
      userProductPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'obligated-subject',
    loadComponent: ()  => import('./core/components/concept-window/concept-window.component').then((m) => m.ConceptWindowComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'obligatedSubject',
      path: 'obligated-subject',
      componentName: 'conceptWindow',
      from: 'subject'
    }
  },
  {
    path: 'snippets',
    loadComponent: () => import('./core/components/snippets-reusable/snippets-reusable.component').then((m) => m.SnippetsReusableComponent),
    canActivate: [JwtAuthGuard, OutSnippetsGuard],
    data: {
      name: 'snippets',
      path: 'snippets',
      componentName: 'snippetsReusable'
    }
  },
  {
    path: 'search',
    loadComponent: () => import('./core/components/global-search/global-search.component').then((m) => m.GlobalSearchComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'search',
      path: 'search',
      componentName: 'globalSearch'
    },
    resolve: {
      initialFilterList: filterListResolver
    }
  },
  {
    path: 'client-evaluation',
    loadComponent: () => import('./core/components/client-evaluation/client-evaluation.component').then((m) => m.ClientEvaluationComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'clientEvaluation',
      path: 'client-evaluation',
      componentName: 'clientEvaluation'
    },
    resolve: {
      companyPermissions: CompanyPermissionsResolver,
    }
  },
  {
    path: 'users',
    loadComponent: () => import('./core/components/users-admin/users-admin.component').then((m) => m.UsersAdminComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'users',
      path: 'users',
      componentName: 'usersAdmin'
    },
    children: [
      {
        path: '',
        loadComponent: () => import('./core/components/users-admin-main/users-admin-main.component').then((m) => m.UsersAdminMainComponent),
        canActivate: [JwtAuthGuard],
        data: {
          name: 'usersMain',
          path: '',
          componentName: 'usersAdminMain'
        }
      },
      {
        path: 'new/user',
        loadComponent: () => import('./core/components/users-admin-new-wizard/users-admin-new-wizard.component').then((m) => m.UsersAdminNewWizardComponent),
        canActivate: [JwtAuthGuard],
        data: {
          breadcrumb:'users-admin.new.title.user',
          name: 'usersNew',
          path: 'new/user',
          type: 'user',
          componentName: 'usersAdminNewWizard'
        }
      },
      {
        path: 'new/contact',
        loadComponent: () => import('./core/components/users-admin-edit/users-admin-edit.component').then((m) => m.UsersAdminEditComponent),
        canActivate: [JwtAuthGuard],
        data: {
          breadcrumb:'users-admin.new.title.contact',
          name: 'contactNew',
          path: 'new/contact',
          type: 'contact',
          operation: 'new',
          componentName: 'usersAdminEdit'
        }
      },
      {
        path: 'new/group',
        loadComponent: () => import('./core/components/users-admin-new-wizard/users-admin-new-wizard.component').then((m) => m.UsersAdminNewWizardComponent),
        canActivate: [JwtAuthGuard],
        data: {
          breadcrumb:'users-admin.new.title.group',
          name: 'groupNew',
          path: 'new/group',
          type: 'group',
          operation: 'new',
          componentName: 'usersAdminNewWizard'
        }
      },
      {
        path: 'edit/:type/:id',
        loadComponent: () => import('./core/components/users-admin-edit/users-admin-edit.component').then((m) => m.UsersAdminEditComponent),
        canActivate: [JwtAuthGuard],
        data: {
          breadcrumb:'common.edit-user',
          name: 'usersEdit',
          path: 'edit/:type/:id',
          operation: 'edit',
          componentName: 'usersAdminEdit'
        }
      },
      {
        path: 'security/:type/:id',
        loadComponent: () => import('./core/components/users-admin-edit/users-admin-edit.component').then((m) => m.UsersAdminEditComponent),
        canActivate: [JwtAuthGuard],
        data: {
          breadcrumb:'users-admin.main.edit-permissions',
          name: 'usersSecurity',
          path: 'security/:type/:id',
          operation: 'security',
          componentName: 'usersAdminEdit'
        }
      },
      {
        path: 'see-security/:type/:id',
        loadComponent: () => import('./core/components/users-admin-edit/users-admin-edit.component').then((m) => m.UsersAdminEditComponent),
        canActivate: [JwtAuthGuard],
        data: {
          breadcrumb:'users-admin.main.consult-permissions',
          name: 'usersSeeSecurity',
          path: 'see-security/:type/:id',
          type: 'user',
          operation: 'see-security',
          componentName: 'usersAdminEdit'
        }
      },
      {
        path: 'convert/:type/:id',
        loadComponent: () => import('./core/components/users-admin-new-wizard/users-admin-new-wizard.component').then((m) => m.UsersAdminNewWizardComponent),
        canActivate: [JwtAuthGuard],
        data: {
          breadcrumb:'users-admin.main.convert-to',
          name: 'usersConvert',
          path: 'convert/:type/:id',
          type: 'contact',
          operation: 'convert',
          componentName: 'usersAdminNewWizard'
        }
      }
    ],
  },
  {
    path: 'alert-list/:evaluationId',
    loadComponent: () => import('./core/components/alert-list-window/alert-list-window.component').then((m) => m.AlertListWindowComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'alertList',
      path: 'alert-list/:evaluationId',
      componentName: 'alertListWindow'
    }
  },
  {
    path: 'client/:clientId',
    loadComponent: ()  => import('./core/components/concept-window/concept-window.component').then((m) => m.ConceptWindowComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'client',
      path: 'client/:clientId',
      componentName: 'conceptWindow',
      from: 'client'
    },
  },
  {
    path: 'report-detail/:reportObjectId',
    loadComponent: () => import('./core/components/report-record/report-record.component').then((m) => m.ReportRecordComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'reportDetail',
      path: 'report-detail/:reportObjectId',
      componentName: 'reportRecordDefault'
    },
    resolve: {
      rows: reportRowsResolve,
      columns: reportColumnsResolve,
      report: reportResolve,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'report-detail/:reportObjectId/archived',
    loadComponent: () => import('./core/components/report-record/report-record.component').then((m) => m.ReportRecordComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'reportDetailArchived',
      path: 'report-detail/:reportObjectId/archived',
      componentName: 'reportRecordDefault',
      archived: 'archived'
    },
    resolve: {
      rows: reportRowsResolve,
      columns: reportColumnsResolve,
      report: reportResolve,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'schedule',
    loadComponent: () => import('./core/components/schedule/schedule.component').then((m) => m.ScheduleComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'schedule',
      path: 'schedule',
      componentName: 'schedule'
    }
  },
  {
    path: 'office-online/:type/:id',
    loadComponent: () => import('./shared/components/office-online/office-online.component').then((m) => m.OfficeOnlineComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'officeOnline',
      path: 'office-online',
      componentName: 'officeOnline'
    },
    resolve: {
      dataOffice: OfficeOnlineResolver
    }
  },
  {
    path: 'shared/task-concept-evaluated/:conceptObjectId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'sharedTaskConcept',
      path: 'shared/task-concept-evaluated/:conceptObjectId',
      componentName: 'landingPage',
      isLanding: true,
      shared: true
    },
    resolve: {
      concept: ConceptObjectResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'shared/concept-evaluated/:conceptObjectId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'sharedConcept',
      path: 'shared/concept-evaluated/:conceptObjectId',
      componentName: 'landingPage',
      isLanding: true,
      shared: true
    },
    resolve: {
      concept: ConceptObjectResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'shared/concept-evaluated/:conceptObjectId/field-container/:fieldContainerId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'sharedFieldContainer',
      path: 'shared/concept-evaluated/:conceptObjectId/field-container/:fieldContainerId',
      componentName: 'landingPage',
      isLanding: true,
      shared: true
    },
    resolve: {
      concept: ConceptObjectResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'shared/concept-preview/:conceptId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'sharedConceptPreview',
      path: 'shared/concept-preview/:conceptId',
      componentName: 'landingPage',
      isLanding: true,
      shared: true
    },
    resolve: {
      concept: ConceptByIdResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'shared/meeting/:meetingId/assist/:assist',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'sharedMeeting',
      path: 'shared/meeting/:meetingId/assist/:assist',
      componentName: 'landingPage',
      exitType: null,
      isLanding: true,
      shared: true
    },
    resolve: {
      assist: AssistResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'shared/task/:taskId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'sharedTask',
      path: 'shared/task/:taskId',
      componentName: 'landingPage',
      isLanding: true,
      shared: true
    },
    resolve: {
      task: TaskResolve,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'shared/download/:documenObjectId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'sharedDownload',
      path: 'shared/download/:documenObjectId',
      componentName: 'landingPage',
      isLanding: true,
      shared: true
    }
  },
  {
    path: 'public/concept/:conceptId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'publicConcept',
      path: 'public/concept/:conceptId',
      componentName: 'landingPage',
      public: true,
      isLanding: true
    },
    resolve: {
      concept: ConceptByIdResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'public/concept-preview/:conceptId',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'publicConceptPreview',
      path: 'public/concept-preview/:conceptId',
      componentName: 'landingPage',
      public: true,
      isLanding: true
    },
    resolve: {
      concept: ConceptByIdResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'public/monitoring',
    component: LandingPageComponent,
    canActivate: [JwtAuthGuard],
    data: {
      name: 'publicMonitoring',
      path: 'public/monitoring',
      componentName: 'landingPage',
      public: true,
      isLanding: true,
      isMonitoring: true
    },
    resolve: {
      concept: ConceptObjectResolver,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'exit',
    loadComponent: () => import('./shared/components/exit-login/exit-login.component').then((m) => m.ExitLoginComponent),
    data: {
      name: 'thankYouPage',
      path: 'exit',
      componentName: 'exitLogin',
      isLanding: true
    }
  },
  {
    path: 'reports/:reportId',
    loadComponent: () => import('./core/components/general-report/general-report.component').then((m) => m.GeneralReportComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'generalReport',
      path: 'reports/:reportId',
      componentName: 'generalReport',
    },
    resolve: {
      reportObjects: reportObjectsListResolve,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'report-list/:reportId',
    loadComponent: () => import('./core/components/report-incidents-list/report-incidents-list.component').then((m) => m.ReportIncidentsListComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'reportIncidentsList',
      path: 'report-list',
      componentName: 'reportIncidentsList',
    },
    resolve: {
      reportList: ReportRecordDetailResolver
    }
  },
  {
    path: 'report-list/:reportId/:evaluationId',
    loadComponent: () => import('./core/components/report-incidents-list/report-incidents-list.component').then((m) => m.ReportIncidentsListComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'reportIncidentsListEvaluation',
      path: 'report-list',
      componentName: 'reportIncidentsList',
    },
    resolve: {
      reportList: ReportRecordDetailResolver
    }
  },
  {
    path: 'reports-evaluation',
    loadComponent: () => import('./core/components/reports-list/report-list-be/report-list-be.component').then((m) => m.ReportListBeComponent),
    canActivate: [JwtAuthGuard],
    data: {
      name: 'reportIncidentsList',
      path: 'reports/evaluation',
      componentName: 'generalReports',
    },
    resolve: {
      filterReportsList: generalReportsWhResolve,
      userPermissions: UserPermissionsResolver
    }
  },
  {
    path: 'contact-login',
    loadComponent: () => import('./core/components/front-login/front-login-landing/front-login-landing.component').then((m) => m.FrontLoginLandingComponent),
    data: {
      name: 'contact-login',
      path: 'contact-login',
      componentName: 'frontLogin',
      isLanding: true
    }
  },
  {
    path: 'change-password/:operation',
    loadComponent: () => import('./core/components/login-change-password/login-change-password.component').then((m) => m.LoginChangePasswordComponent),
    canActivate: [ChangePasswordGuard],
    data: {
      name: 'change-password',
      path: 'change-password',
      componentName: 'loginChangePassword',
    }
  },
  {
    path: 'change-password-error',
    loadComponent: () => import('./core/components/login-change-password/login-change-password.component').then((m) => m.LoginChangePasswordComponent),
    data: {
      name: 'change-password-error',
      path: 'change-password-error',
      componentName: 'loginChangePassword',
      hasError: true
    },
  },
  ...devRoutes,
  {
    path: 'politica-de-cookies',
    loadComponent: () => import('./shared/components/cookies-policy/cookies-policy.component').then((m) => m.CookiesPolicyComponent)
  },
  {
    path: '**',
    redirectTo: 'dashboard'
  }
]

export const routerConfig: ExtraOptions = {
  paramsInheritanceStrategy: 'always',
  onSameUrlNavigation: 'reload',
  preloadingStrategy: 'PreloadAllModules'
}
